import BaseStore from 'bz-stores/mobx/base-store';
import useComputed from 'bz-utilities/hooks/computed';
import { type IObservableArray, action, computed, observable } from 'mobx';
import {
  type FC,
  type PropsWithChildren,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { FeatureRequest, type LicenceModule, ModularFeaturesRequest } from 'tm3-core/api.dtos';
import type ModuleEnum from 'tm3-core/licence';

interface IProps extends PropsWithChildren {
  authenticated: boolean;
}

export enum LicenceType {
  UserLicence = 'User',
  DiaryLicence = 'Diary',
  FixedLicence = 'Fixed'
}

export const LicenceTypeContext = createContext<string>([]);
export const FeatureFlagsContext = createContext<string[]>([]);
export const AllowedModulesContext = createContext<(module: ModuleEnum) => boolean>(() => false);
export const ModuleFeatureContext = createContext<LicenceModule[]>([]);

export const useAllowed = () => {
  return useContext(AllowedModulesContext);
};
class ObservableModuleFeatureStore {
  @observable modules: IObservableArray<LicenceModule> = observable.array([]);

  @computed
  get allowedModules() {
    return this.modules.map((l) => l.name);
  }

  load(data: LicenceModule[]) {
    this.modules.replace(data);
  }
  @action.bound hasAllowedModule(module: ModuleEnum) {
    return this.allowedModules.includes(module);
  }
}

export const ModuleFeatureObservable = new ObservableModuleFeatureStore();

const ModuleFeatureProvider: FC<IProps> = memo(({ children, authenticated }) => {
  const [flags, setFlags] = useState<string[]>([]);
  const [licenceType, setLicenceType] = useState<string>('');
  const [modularFeatures, setModularFeatures] = useState<LicenceModule[]>([]);

  const allowedModules = useMemo(() => modularFeatures.map((l) => l.name), [modularFeatures]);

  const hasAllowedModule = useCallback(
    (module: ModuleEnum) => allowedModules.includes(module),
    [allowedModules]
  );

  const onLoadFlags = useCallback(async () => {
    const res = await BaseStore.defaultClient.get(new FeatureRequest());
    setFlags(Object.keys(res.result).filter((key) => res.result[key] === 'TRUE'));
  }, []);

  const onLoadModularFeatures = useCallback(async () => {
    const res = await BaseStore.defaultClient.get(new ModularFeaturesRequest());
    setModularFeatures(res.result.modules);
    setLicenceType(res.result.type);
    ModuleFeatureObservable.load(res.result.modules);
  }, []);

  useEffect(() => {
    if (authenticated) {
      onLoadFlags();
      onLoadModularFeatures();
    }
  }, [authenticated, onLoadFlags, onLoadModularFeatures]);

  return (
    <ModuleFeatureContext.Provider value={modularFeatures}>
      <LicenceTypeContext.Provider value={licenceType}>
        <AllowedModulesContext.Provider value={hasAllowedModule}>
          <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>
        </AllowedModulesContext.Provider>
      </LicenceTypeContext.Provider>
    </ModuleFeatureContext.Provider>
  );
});

export default ModuleFeatureProvider;
